import React, { useEffect, useState } from "react";
import {
  getOverallLogs,
  OverallLogDTO,
  getAllUsers
} from "../../Api/ApiUrlConst";
import { Input, Table } from "reactstrap";

/* ---------- Filter Options ---------- */
const entityOptions = [
  { value: "", label: "All" },
  { value: "Project", label: "Project" },
  { value: "Task", label: "Task" },
  { value: "SubTask", label: "SubTask" },
  { value: "ChildTask", label: "Child Task" },
];

const actionOptions = [
  { value: "", label: "All" },
  { value: "Create", label: "Create" },
  { value: "Update", label: "Update" },
  { value: "Delete", label: "Delete" },
];

const OverallLogPage: React.FC = () => {
  const [logs, setLogs] = useState<OverallLogDTO[]>([]);
  const [loading, setLoading] = useState(false);

  /* ---------- Current Month Range ---------- */
 const getCurrentMonthRange = () => {
  const now = new Date();

  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  const format = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  return {
    from: format(firstDay),
    to: format(lastDay),
  };
};


  const monthRange = getCurrentMonthRange();

  /* ---------- Filters ---------- */
  const [entityType, setEntityType] = useState("");
  const [action, setAction] = useState("");
  const [userId, setUserId] = useState("");
  const [fromDate, setFromDate] = useState(monthRange.from);
  const [toDate, setToDate] = useState(monthRange.to);

  const [users, setUsers] = useState<{ userID: string; name: string }[]>([]);

  /* ---------- Excluded Users ---------- */
   const excludedEmails = [
    "client@wocons.com",
    "itmanager@gmail.com",
    "krish@gmail.com",
    "renganathan@wocons.com",
    "gokul@feesworth.com",
    "muthupandu400@gmail.com",
    "ravindrababu@wocons.com",
    "santhoshs@wocons.com",
    "santhoshr@wocons.com",
    "itmanager@gmail.com",
    "client@wocons.com",
    "hr@wocons.com",
    "vishnu@wocons.com",
    "limya@wocons.com",
    "Perumal@wocons.com",
    "krishsengamuthu@gmail.com",
    "gokulkannan199805@gmail.com",
    "projectmanager@wocons.com",
    "Rizalde.Barrato@Donaldson.com",
    "Vishnu.Prasath@Donaldson.com",
    "limya.shilin1@donaldson.com"
  ];

  /* ---------- Load Users ---------- */
  const loadUsers = async () => {
    try {
      const res = await getAllUsers();
      const data = res.data?.$values || res.data || [];

      const filteredUsers = data
        .filter((u: any) => {
          const email =
            u.email ||
            u.emailId ||
            u.emailAddress ||
            "";

          return !excludedEmails.includes(email);
        })
        .map((u: any) => ({
          userID: u.userID,
          name: u.name,
        }));

      setUsers(filteredUsers);
    } catch {
      setUsers([]);
    }
  };

  /* ---------- Fetch Logs ---------- */
const fetchLogs = async () => {
  setLoading(true);

  try {
    // safe dates without timezone problems
    const from = fromDate ? new Date(fromDate) : null;
    const to = toDate ? new Date(toDate) : null;

    if (to) {
      to.setDate(to.getDate() + 1); // include full end date
    }

    const res = await getOverallLogs({
      entityType: entityType || undefined,
      action: action || undefined,
      userId: userId || undefined,
      from: from ? from.toISOString() : undefined,
      to: to ? to.toISOString() : undefined,
    });

    setLogs(res.data || []);
  } catch (error) {
    console.error("Error fetching logs", error);
    setLogs([]);
  }

  setLoading(false);
};



  /* ---------- Initial Load ---------- */
  useEffect(() => {
    loadUsers();
  }, []);

  /* ---------- Auto Fetch ---------- */
  useEffect(() => {
    fetchLogs();
  }, [entityType, action, userId, fromDate, toDate]);

  /* ---------- Date Format ---------- */
  const formatLogDate = (dateString: string) => {
    const date = new Date(dateString);

    const formattedDate = date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

    const formattedTime = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });

    return `${formattedDate} ${formattedTime}`;
  };

  return (
    <div className="p-6 space-y-6">
      {/* ---------- Filters ---------- */}
      <div className="overflow-x-auto bg-white shadow-lg rounded-lg mb-6">
        <Table borderless>
          <thead>
            <tr>
              <th>Module</th>
              <th>Action</th>
              <th>User</th>
              <th>From</th>
              <th>To</th>
            </tr>
          </thead>

          <tbody>
            <tr>
              <td>
                <select
                  className="border p-2 rounded w-full"
                  value={entityType}
                  onChange={(e) => setEntityType(e.target.value)}
                >
                  {entityOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </td>

              <td>
                <select
                  className="border p-2 rounded w-full"
                  value={action}
                  onChange={(e) => setAction(e.target.value)}
                >
                  {actionOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </td>

              <td>
                <select
                  className="border p-2 rounded w-full"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                >
                  <option value="">All Users</option>
                  {users.map((u) => (
                    <option key={u.userID} value={u.userID}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </td>

              <td>
                <Input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </td>

              <td>
                <Input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </td>
            </tr>
          </tbody>
        </Table>
      </div>

      {/* ---------- Logs ---------- */}
      <div className="overflow bg-white shadow-lg rounded-lg p-4">
        <Table striped>
          <thead>
            <tr>
              <th style={{ width: "120px" }}>Module</th>
        <th style={{ width: "150px" }}>Field</th>
        <th style={{ width: "100px" }}>Action</th>
        <th style={{ width: "220px" }}>Old</th>
        <th style={{ width: "220px" }}>New</th>
        <th style={{ width: "150px" }}>User</th>
        <th style={{ width: "170px" }}>Time</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="text-center p-4">
                  Loading...
                </td>
              </tr>
            ) : logs.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center p-4">
                  No logs found
                </td>
              </tr>
            ) : (
              logs.map((log) => (
                <tr key={log.id}>
                  <td>{log.entityType}</td>
                  <td>{log.fieldName}</td>
                  <td>{log.action}</td>
                  <td className="text-red-600">{log.oldValue ?? "-"}</td>
                  <td className="text-green-600">{log.newValue ?? "-"}</td>
                  <td>{log.userName}</td>
                  <td>{formatLogDate(log.logTime)}</td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default OverallLogPage;
